import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EXPLORE_TEMPLATE_BY_SEGMENT } from "@majourneys/shared";
import type { DiscoverItem } from "../types";
import {
  type ItineraryStop,
  type ListSection,
  type Locale,
  buildListSections,
  formatDuration,
  getItineraryStops,
  pickLocalized,
  pickupIsExcluded,
} from "../lib/content-blocks";

import { Icon } from "@majourneys/icons";
interface Props {
  readonly item: DiscoverItem;
  readonly locale?: Locale;
  readonly overviewHtml?: string;
}

type TabKey = "overview" | "highlights" | "itinerary" | "includes" | "good-to-know";

interface TabDef {
  key: TabKey;
  label: string;
  icon: string;
}

function stripHtml(html: string | undefined | null): string {
  if (!html) return "";
  const noScripts = html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ");
  return noScripts.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function looksLikeHtml(value: string): boolean {
  return /<\/?(p|div|h[1-6]|ul|ol|li|strong|em|br|span|a)\b/i.test(value);
}

export function DiscoverContentBlocks({ item, locale = "en", overviewHtml }: Props) {
  const { t } = useTranslation();
  const template = item.template ?? EXPLORE_TEMPLATE_BY_SEGMENT[item.segment];
  const blocks = item.contentBlocks ?? {};

  const itineraryStops = useMemo(
    () => getItineraryStops(template, blocks),
    [template, blocks],
  );
  const listSections = useMemo(
    () => buildListSections(template, blocks, locale),
    [template, blocks, locale],
  );

  const sectionByKey = useMemo(
    () => new Map(listSections.map((s) => [s.key, s])),
    [listSections],
  );
  const highlights = sectionByKey.get("highlights");
  const included = sectionByKey.get("included");
  const excluded = sectionByKey.get("excluded");

  const goodToKnowSections = useMemo<ListSection[]>(() => {
    const groupedKeys = new Set(["highlights", "included", "excluded"]);
    const order = [
      "pickupInfo",
      "propertyDetails",
      "bestFor",
      "amenities",
      "roomFeatures",
      "services",
      "safety",
      "accessibility",
      "checkInOut",
      "houseRules",
      "menuHighlights",
      "dietaryInfo",
      "seatingInfo",
      "scheduleInfo",
      "accessInfo",
      "whatToBring",
      "infoBeforeBooking",
      "knowBeforeYouGo",
      "notSuitable",
      "notAllowed",
      "whyVisit",
      "whatToExpect",
      "bestTime",
      "howToCombine",
      "openingHours",
      "practicalTips",
      "whyEat",
      "cuisine",
      "priceRange",
      "whyStay",
      "nearby",
      "whyBook",
      "treatments",
      "overview",
      "stops",
    ];
    const ordered = order
      .map((k) => sectionByKey.get(k))
      .filter((s): s is ListSection => Boolean(s));
    const orderedKeys = new Set(ordered.map((s) => s.key));
    const extras = listSections.filter(
      (s) => !groupedKeys.has(s.key) && !orderedKeys.has(s.key),
    );
    return [...ordered, ...extras];
  }, [listSections, sectionByKey]);

  const hasOverview = stripHtml(overviewHtml ?? "").length > 0;

  const tabs = useMemo<TabDef[]>(() => {
    const list: TabDef[] = [];
    if (hasOverview) {
      list.push({ key: "overview", label: t("discover_blocks.tab_overview"), icon: "ri-compass-3-line" });
    }
    if (highlights) {
      list.push({ key: "highlights", label: highlights.title, icon: "ri-sparkling-line" });
    }
    if (itineraryStops.length > 0) {
      list.push({ key: "itinerary", label: t("discover_blocks.tab_itinerary"), icon: "ri-route-line" });
    }
    if (included || excluded) {
      list.push({ key: "includes", label: t("discover_blocks.tab_includes"), icon: "ri-checkbox-circle-line" });
    }
    if (goodToKnowSections.length > 0) {
      list.push({ key: "good-to-know", label: t("discover_blocks.tab_good_to_know"), icon: "ri-lightbulb-line" });
    }
    return list;
  }, [hasOverview, highlights, itineraryStops.length, included, excluded, goodToKnowSections.length, t]);

  const [active, setActive] = useState<TabKey>(() => tabs[0]?.key ?? "overview");

  if (tabs.length === 0) return null;
  const activeTab = tabs.find((t) => t.key === active) ? active : tabs[0]!.key;

  return (
    <section className="gd-blocks">
      <div className="gd-blocks__pills" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`gd-block-pill${activeTab === tab.key ? " is-active" : ""}`}
            onClick={() => setActive(tab.key)}
          >
            <Icon name={tab.icon} aria-hidden="true" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="gd-blocks__panel">
        {activeTab === "overview" && hasOverview ? (
          looksLikeHtml(overviewHtml!) ? (
            <div
              className="gd-overview gd-html"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: overviewHtml! }}
            />
          ) : (
            <div className="gd-overview">
              {overviewHtml!
                .split(/\n{2,}/)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
            </div>
          )
        ) : null}

        {activeTab === "highlights" && highlights ? (
          <HighlightsList items={highlights.items} />
        ) : null}

        {activeTab === "itinerary" && itineraryStops.length > 0 ? (
          <ItineraryList stops={itineraryStops} locale={locale} />
        ) : null}

        {activeTab === "includes" && (included || excluded) ? (
          <IncludesExcludes included={included} excluded={excluded} />
        ) : null}

        {activeTab === "good-to-know" && goodToKnowSections.length > 0 ? (
          <GoodToKnow
            sections={goodToKnowSections}
            excludedItems={excluded?.items ?? []}
          />
        ) : null}
      </div>
    </section>
  );
}

function HighlightsList({ items }: { items: string[] }) {
  return (
    <ul className="gd-highlights">
      {items.map((item, i) => (
        <li key={i} className="gd-highlight">
          <span className="gd-highlight__num">{String(i + 1).padStart(2, "0")}</span>
          <span className="gd-highlight__text">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ItineraryList({
  stops,
  locale,
}: {
  stops: ItineraryStop[];
  locale: Locale;
}) {
  return (
    <ol className="gd-itinerary">
      {stops.map((stop, idx) => {
        const title = pickLocalized(stop.title, locale);
        const desc = pickLocalized(stop.description, locale);
        const dur = formatDuration(stop.durationMinutes);
        const tags = (stop.tags ?? []).filter((tag) => tag.trim().length > 0);
        const isLast = idx === stops.length - 1;
        return (
          <li key={idx} className={`gd-stop${isLast ? " is-last" : ""}`}>
            <div className="gd-stop__rail">
              <span className="gd-stop__num">{idx + 1}</span>
              {!isLast && <span className="gd-stop__line" />}
            </div>
            <div className="gd-stop__body">
              <h5 className="gd-stop__title">{title || `Stop ${idx + 1}`}</h5>
              {(dur || tags.length > 0) && (
                <div className="gd-stop__chips">
                  {dur && <span className="gd-chip gd-chip--time">{dur}</span>}
                  {tags.map((tag) => (
                    <span key={tag} className="gd-chip">{tag}</span>
                  ))}
                </div>
              )}
              {desc && <p className="gd-stop__desc">{desc}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function IncludesExcludes({
  included,
  excluded,
}: {
  included: ListSection | undefined;
  excluded: ListSection | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="gd-includes">
      {included && (
        <ColumnList title={included.title || t("discover_blocks.included")} items={included.items} tone="positive" />
      )}
      {excluded && (
        <ColumnList title={excluded.title || t("discover_blocks.not_included")} items={excluded.items} tone="negative" />
      )}
    </div>
  );
}

function ColumnList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "positive" | "negative";
}) {
  return (
    <div className={`gd-list gd-list--${tone}`}>
      <header className="gd-list__header">
        <span className="gd-list__symbol" aria-hidden="true">
          <Icon name={tone === "positive" ? "ri-check-line" : "ri-close-line"} />
        </span>
        <h5 className="gd-list__title">{title}</h5>
        <span className="gd-list__count">{items.length}</span>
      </header>
      <ul className="gd-list__items">
        {items.map((item, i) => (
          <li key={i} className="gd-list__item">
            <span className="gd-list__bullet" aria-hidden="true">
              <Icon name={tone === "positive" ? "ri-check-line" : "ri-close-line"} />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GoodToKnow({
  sections,
  excludedItems,
}: {
  sections: ListSection[];
  excludedItems: string[];
}) {
  const { t } = useTranslation();
  const noPickup = pickupIsExcluded(excludedItems);
  return (
    <div className="gd-gtk">
      {sections.map((section) => {
        const isPickup = section.key === "pickupInfo";
        const renderAsMeetingPoint = isPickup && noPickup;
        const headerTitle = renderAsMeetingPoint ? t("activity.meeting_point", { defaultValue: "Meeting point" }) : section.title;
        const tone = section.tone;
        return (
          <article key={section.key} className={`gd-gtk__card gd-gtk__card--${tone}`}>
            <header className="gd-gtk__header">
              <span className="gd-gtk__icon" aria-hidden="true">
                <Icon name={section.icon} />
              </span>
              <h5 className="gd-gtk__title">{headerTitle}</h5>
              {renderAsMeetingPoint && (
                <span className="gd-gtk__pill">{t("activity.no_pickup", { defaultValue: "No pickup" })}</span>
              )}
            </header>
            {renderAsMeetingPoint && (
              <p className="gd-gtk__hint">
                {/* Was English on every page, with an em dash (owner, 2026-10-11). */}
                {t("activity.no_pickup_hint", { defaultValue: "Pickup is not included, please make your own way to the meeting point." })}
              </p>
            )}
            <ul className="gd-gtk__items">
              {section.items.map((item, i) => (
                <li key={i} className="gd-gtk__item">
                  <span className={`gd-gtk__bullet gd-gtk__bullet--${tone}`} aria-hidden="true">
                    {/* The site's icons, not typed characters (owner, 2026-10-11). */}
                    <Icon name={tone === "positive" ? "ri-check-line" : tone === "negative" ? "ri-close-line" : "ri-subtract-line"} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        );
      })}
    </div>
  );
}
